import OrderMergeSplitStatus from "@/enum/OrderMergeSplitStatus";
import OrderStatus from "@/enum/OrderStatus";
import { get, post } from "../http/platformServer";
import LogisticsCompany from "@/enum/LogisticsCompany";
import LogisticsInfoStatus from "@/enum/LogisticsInfoStatus";
import OrderSellerRemarksFlag from "@/enum/OrderSellerRemarksFlag";
import OrderDTO from "@/dto/order/OrderDTO";
import OrderItemDTO from "@/dto/order/OrderItemDTO";
import FulfillmentType from "@/enum/FulfillmentType";
import OrderTab from "@/scenes/OrderManagement/enum/OrderTab";
import TimeType from "@/scenes/OrderManagement/enum/TimeType";
import EwaybillDTO from "@/dto/order/EwaybillDTO";
import OrderConsigneeDTO from "@/dto/order/OrderConsigneeDTO";
import WaybillTemplateDTO from "@/dto/template/WaybillTemplateDTO";
import PackingListTemplateDTO from "@/dto/template/PackingListTemplateDTO";
import OrderItemLabelTemplateDTO from "@/dto/template/OrderItemLabelTemplateDTO";
import { cloneDeep, uniq, uniqBy } from "lodash";
import ManualOrderConsigneeDTO from "@/dto/manualOrder/ManualOrderConsigneeDTO";
import OrderSellerRemarksDTO from "@/dto/order/OrderSellerRemarksDTO";
import OrderSort from "@/scenes/OrderManagement/enum/OrderSort";
import AffiliateMarketerDto from "@/dto/AffiliateMarketerDto";
import mainServerClient from "../http/mainServerClient";
import { PremiumFunctionType } from "../PremiumValidator";
import { retry } from "@/utils/retry";
import { getOrderOrderItems, sortEwaybills } from "@/utils/OrderUtils";
import QuickEnum from "@/scenes/OrderManagement/enum/QuickEnum";
import TaskStatusEnum from "@/enum/TaskStatus";
import AbnormalOrderRecordType from "@/enum/AbnormalOrderRecordType";
import { PrintShippingSource, ShippingAddressSelectType } from "./ewaybillGenerate";
import { showWrongOrderStateModal } from "@/utils/dialog";
import LoadOrderError, { LoadOrderErrorCode } from "./exception/LoadOrderError";
import GlobalStore from "@/store";

// 订单列表 order/list
// 订单搜索 order/search
// 更新订单备注  order/updateSellerRemarks
// 订单批量更新备注 order/batchUpdateSellerRemarks
// 使用原地址 order/deleteLocalConsignee
// 订单发货 order/ship
// api.order.list().then(orders => {)
// 订单列表 get orders ,参数 shopId pageNo pageSize
// 订单搜索 get orders/:search
// 更新订单备注 put orders/{id}:updateSellerRemarks
// 发货 post orders/{id}:ship

/**重新给orderItem排序 */
function getOrderItemStatusSortIndex(orderItem: OrderItemDTO) {
    switch (orderItem.status) {
        case OrderStatus.PENDING_PAYMENT:
            return 1;
        case OrderStatus.PENDING_SHIPMENT:
            return 2;
        case OrderStatus.SHIPPED:
            return 3;
        case OrderStatus.SUCCESS:
            return 4;
        case OrderStatus.CANCELED:
            return 5;

        default:
            break;
    }

    if (orderItem.isRefunding) {
        return 6;
    }

    return 99;
}

function isHistoryOrder(order: OrderDTO) {
    return !!order.isHistoryOrder;
}

/**
 * 商品信息根据状态来排序
 */
export const sortOrderItemsByStatus = (orderItems: OrderItemDTO[]) => {
    if (!orderItems || orderItems.length === 1) {
        return orderItems;
    }

    const originalIndexMap = {};

    // fix: 当修改订单内orderItem信息(定制信息)之后，会导致原来的顺序错乱。
    orderItems.sort((a, b) => {
        return a.id - b.id;
    });

    orderItems.forEach((orderItem, index) => {
        originalIndexMap[orderItem.id] = index;
    });

    return orderItems.sort((a, b) => {
        if (a.status === b.status) {
            return originalIndexMap[a.id] - originalIndexMap[b.id]; // 确保原顺序
        }

        return getOrderItemStatusSortIndex(a) - getOrderItemStatusSortIndex(b);
    });
};
/**
 * 电子面单排序
 * 1、主运单号排在第一个（后台排序）
 * 2、多个运单号按打印时间倒序排列
 */
export const sortEwaybillNos = (order: OrderDTO) => {
    sortEwaybills(order.ewaybills, order.waybillNo);
};

export const buildParentOrderItems = (childOrders: OrderDTO[]) => {
    const orderItems: OrderItemDTO[] = [];

    childOrders.forEach((childOrder) => {
        if (childOrder.orderItems) {
            childOrder.orderItems.forEach((orderItem) => {
                orderItems.push(orderItem);
            });
        }
    });

    return orderItems;
};

/**去除对象内字符串空格、对象空值 */
const trimParams = (params: any) => {
    const newParams = {};

    Object.keys(params).forEach((key) => {
        const originalValue = params[key];
        if (typeof originalValue === "string") {
            const value = originalValue.trim();

            if (value) {
                newParams[key] = value;
            }
        } else if (typeof originalValue === "number") {
            newParams[key] = originalValue;
        } else if (typeof originalValue === "boolean") {
            newParams[key] = originalValue;
        } else if (Array.isArray(originalValue)) {
            if (originalValue.length !== 0) {
                newParams[key] = originalValue;
            }
        } else {
            if (originalValue) {
                newParams[key] = originalValue;
            }
        }
    });

    return newParams;
};

interface MultiShopOrderListParams extends Omit<QuickSearchOrderParams, "pageNo" | "pageSize" | "shopId"> {
    /** offset从0开始(pageNo + pageSize 分页 与 offset + limit 分页 2选一) */
    offset: number;

    limit: number;

    shopIds: number[];
}

interface QueryOrderListCommonParams {
    shopId: number;
    tab: OrderTab;
    orderSort?: OrderSort;
    fulfillmentType?: FulfillmentType;
    assigneeId?: number;
    buyerRemarks?: string;
    buyerUsername?: string;
    cityCode?: number;
    consigneeName?: string;
    districtCode?: number;
    itemQuantityFrom?: number;
    itemQuantityTo?: number;
    logisticsCompany?: LogisticsCompany;
    logisticsInfoStatus?: LogisticsInfoStatus;
    maxPayment?: number;
    maxWeight?: number;
    minPayment?: number;
    minWeight?: number;
    originalIds?: string[];
    phone?: string;
    printStatus?: string;
    provinceCode?: number;
    quick?: number;
    sellerFlag?: OrderSellerRemarksFlag;
    sellerRemarks?: string;
    shippingAreaId?: number;
    street?: string;
    /**
     * yyyy-MM-dd HH:mm
     */
    timeFrom?: string;
    /**
     * yyyy-MM-dd HH:mm
     */
    timeTo?: string;
    timeType?: TimeType;
    waybillNo?: string;
    skuOriginalId?: string;
    /**
     * 指定特定的订单标记 使用 ',' 分割
     */
    markIds?: string;
    orderItemExternalId?: string;
    orderItemTitle?: string;
    productOriginalId?: string;
    retailerDropShippingInfoParams?: {
        retailerShopIds?: number[];
    };
    sellerFlags?: OrderSellerRemarksFlag;
    shippingAddressId?: number;
    shippingArea?: {
        excludedCityCodes: number[];
        excludedDistrictCodes: number[];
        includedProvinceCodes: number[];
    };
    skuProperties?: string;
    /**仓库货位 */
    stockNo?: string;
    undeliverableArea?: {
        excludedProvinces?: number[];
        excludedCities?: number[];
        excludedDistricts?: number[];
        includedProvinces?: number[];
        includedCities?: number[];
        includedDistricts?: number[];
    };
    supplierDropShippingInfoParams?: {
        supplierShopId?: number;
    };
    needTotalCount?: boolean;

    /** 单选/多选模式下，选中的skuInfoIds */
    titleSkuInfoIds?: number[];
    skuPropertiesSkuInfoIds?: number[];
    externalIdSkuInfoIds?: number[];
    allMatchQuickCriterionList?: QuickEnum[];
    anyMatchQuickCriterionList?: QuickEnum[];
    skuOriginalIds?: string[];
    productOriginalIds?: string[];
    abnormalOrderRecordTypes: AbnormalOrderRecordType[];
    /**
     * 物流停滞小时数阈值（12/24/48/72 或自定义）
     * 如果不为空，查询最新物流活动时间距今超过该小时数的订单
     */
    logisticsStagnantHours?: number;
    printBatchNos?: string[];
    itemStatuses?: OrderStatus[];
    includeAffiliateMarketer?: boolean;
    /** 前端中间字段，请求前按店铺拆成 affiliateMarketerIds */
    affiliateMarketerKeys?: string[];
    affiliateMarketerIds?: number[];
    remainingShippingHours?: number;
}

interface QueryOrderListParamsWithPageSize extends QueryOrderListCommonParams {
    pageNo: number;
    pageSize: number;
}

interface QueryOrderListParamsWithOffset extends QueryOrderListCommonParams {
    offset: number;
    limit: number;
}

export type QueryOrderListParams = QueryOrderListParamsWithPageSize | QueryOrderListParamsWithOffset;

/**
 * 订单列表统计对象
 */
export interface OrderListCount {
    /**
     * 前端值
     * 目前仅在单店铺场景中使用
     * 主要是用于快速切换店铺获取OrderListCount时，标识OrderListCount属于哪一个店铺
     */
    shopId?: number;

    /**
     * 前端值
     * 目前仅在单店铺场景中使用
     * 标识OrderListCount属于Tab请求的
     */
    tab?: OrderTab;

    /**
     * 数据库里当前最后异常订单记录创建时间
     */
    lastAbnormalOrderRecordCreatedTime?: number;

    /**待发货订单总数（计算平台订单） */
    totalPendingShipmentOrders?: number;

    /**挂起订单总数 */
    totalOnHoldOrders?: number;

    /**
     * @deprecated
     */
    totalRefundingOrders?: number;

    /**待付款订单退款中订单数 */
    totalPendingShipmentRefundingOrders?: number;

    /**已发货订单退款中订单数, 仅退款中TAB才会获取该值 */
    totalShippedRefundingOrders?: number;

    /**待审核订单数（供应商） */
    totalPendingApprovalOrders?: number;

    /**待处理订单数（供应商） */
    totalProcessingOrders?: number;

    /**是否存在异常订单 */
    hasAbnormalOrders?: boolean;

    /**物流异常预警订单数 */
    totalLogisticsWarningOrders?: number;

    /**预发货订单数 */
    preShipOrders?: number;

    /**
     * 待发货即将发货超时的订单总数
     */
    shippingDeadlinePendingShipmentOrderCount?: number;

    /**
     * 挂起即将发货超时的订单总数
     */
    shippingDeadlineOnHoldOrderCount?: number;

    /**
     * 退款中即将发货超时的订单总数
     */
    shippingDeadlineRefundingOrderCount?: number;

    /**
     * 发货已超时的待发货订单总数
     */
    shippingOverduePendingShipmentOrderCount?: number;

    /**
     * 发货已超时的挂起订单总数
     */
    shippingOverdueOnHoldOrderCount?: number;

    /**
     * 发货已超时的退款中订单总数
     */
    shippingOverdueRefundingOrderCount?: number;

    /**
     * 该店铺即将揽收超时订单总数
     * 订单发货后即将超过平台规定揽收时间还没有物流轨迹的订单数量
     */
    pickupDeadlineWarningOrderCount?: number;
}

// TODO QueryOrderListResult属性非可选
export interface QueryOrderListResult extends OrderListCount {
    orders: OrderDTO[];
    totalCount: number;
}

export interface LogReprintWayBillParams {
    copies: number;
    ewaybills: EwaybillDTO[];
    logisticsCompany: string;
    order: OrderDTO;
    templateName: string;
    isGenerateWaybillNoOnly?: boolean;
    /**
     * 调用来源
     */
    invocationSource?: PrintShippingSource;
    generateAndPrintSameTime?: boolean;
    ignoreDuplicateOrderValidation?: boolean;
    printerName: string;
    shippingAddressSelectType?: ShippingAddressSelectType;
}

export interface MergeOrderParams {
    /** 表示waybillNo是否为电子面单号 */
    isEwaybill: boolean;
    isPackingListPrinted: boolean;
    isWaybillPrinted: boolean;
    logisticsCompany: LogisticsCompany;
    orderIds: number[];
    shopId: number;
    /**目标订单，用这个订单的地址作为合并订单的地址 */
    targetOrderId: number;
    waybillNo: string;
}

export function validateBackendOrder(order: OrderDTO, throwOnError = true) {
    if (!order) return;

    const useNewValidator = [OrderStatus.PENDING_SHIPMENT].includes(order.status);

    if (!useNewValidator) {
        try {
            if (
                order.mergeSplitStatus === OrderMergeSplitStatus.MERGE_CHILD ||
                order.mergeSplitStatus === OrderMergeSplitStatus.SPLIT_PARENT
            ) {
                throw new Error(
                    `订单数据异常,code: ORDER_SPLIT_MERGE_STATE_INVALID,shopId:${order.shopId},orderId:${order.id})`,
                );
            }

            if (order.mergeSplitStatus !== OrderMergeSplitStatus.MERGE_PARENT) return;

            if (!order.childOrders?.length) {
                throw new Error(
                    `订单数据异常,code: MERGE_ORDER_NO_CHILDREN,shopId:${order.shopId},orderId:${order.id})`,
                );
            }

            if (order.childOrders.length === 1) {
                throw new Error(
                    `订单数据异常,code: MERGE_ORDER_SINGLE_CHILD,shopId:${order.shopId},orderId:${order.id})`,
                );
            }

            const parentItems = order.orderItems || [];
            const childOrders = order.childOrders || [];

            // 统计子订单商品
            const childProductMap = new Map<string, number>();
            let childTotalQty = 0;

            childOrders.forEach((child) => {
                child.orderItems?.forEach((item) => {
                    const key = item.originalId;
                    const currentQty = childProductMap.get(key) || 0;
                    childProductMap.set(key, currentQty + item.quantity);
                    childTotalQty += item.quantity;
                });
            });

            // 统计父订单商品
            const parentProductMap = new Map<string, number>();
            let parentTotalQty = 0;

            parentItems.forEach((item) => {
                const key = item.originalId;
                const currentQty = parentProductMap.get(key) || 0;
                parentProductMap.set(key, currentQty + item.quantity);
                parentTotalQty += item.quantity;
            });

            // 校验 3：总数量是否一致
            if (parentTotalQty !== childTotalQty) {
                throw new Error(
                    `订单数据异常,code: MERGE_ORDER_QUANTITY_MISMATCH,shopId:${order.shopId},orderId:${order.id})`,
                );
            }

            // 校验 4：种类及每种数量是否一致
            // 首先看种类数量是否一致
            if (parentProductMap.size !== childProductMap.size) {
                throw new Error(
                    `订单数据异常,code: MERGE_ORDER_ITEM_SIZE_MISMATCH,shopId:${order.shopId},orderId:${order.id})`,
                );
            }

            // 细化比对每一种商品的数量
            for (const [orderItemOriginalId, childQty] of childProductMap.entries()) {
                const parentQty = parentProductMap.get(orderItemOriginalId);
                if (parentQty === undefined) {
                    throw new Error(
                        `订单数据异常,code: MERGE_ORDER_MISS_ITEM,shopId:${order.shopId},orderId:${order.id})`,
                    );
                }
                if (parentQty !== childQty) {
                    throw new Error(
                        `订单数据异常,code: MERGE_ORDER_ITEM_MISMATCH,shopId:${order.shopId},orderId:${order.id})`,
                    );
                }
            }
        } catch (error) {
            order._weirdInfo = error?.message;

            if (throwOnError) {
                throw error;
            }
        }
    } else {
        try {
            // 避免出现不能显示的订单状态
            if (
                order.mergeSplitStatus === OrderMergeSplitStatus.MERGE_CHILD ||
                order.mergeSplitStatus === OrderMergeSplitStatus.SPLIT_PARENT
            ) {
                const msg = `订单数据异常,code: ORDER_SPLIT_MERGE_STATE_INVALID,shopId:${order.shopId},orderId:${order.id})`;
                throw new LoadOrderError(LoadOrderErrorCode.ORDER_SPLIT_MERGE_STATE_INVALID, msg);
            }

            if (order.mergeSplitStatus === OrderMergeSplitStatus.MERGE_PARENT) {
                const childOrderLength = order.childOrders?.length;

                if (!childOrderLength) {
                    const msg = `订单数据异常,code: MERGE_ORDER_NO_CHILDREN,shopId:${order.shopId},orderId:${order.id})`;
                    throw new LoadOrderError(LoadOrderErrorCode.MERGE_ORDER_NO_CHILDREN, msg);
                }

                if (childOrderLength === 1) {
                    const msg = `订单数据异常,code: MERGE_ORDER_SINGLE_CHILD,shopId:${order.shopId},orderId:${order.id})`;
                    throw new LoadOrderError(LoadOrderErrorCode.MERGE_ORDER_SINGLE_CHILD, msg);
                }

                // 至少要有一个子订单的状态和订单的状态一致
                if (!order.childOrders.some(({ status }) => status === order.status)) {
                    const msg = `订单数据异常,code: MERGE_ORDER_CHILD_STATUS_CONFLICT,shopId:${order.shopId},orderId:${order.id})`;

                    throw new LoadOrderError(LoadOrderErrorCode.MERGE_ORDER_CHILD_STATUS_CONFLICT, msg);
                }

                const orderOriginalIdSet = new Set(order.childOrders.map(({ originalId }) => originalId));
                if (orderOriginalIdSet.size !== childOrderLength) {
                    // 有重复的合并子订单
                    const msg = `订单数据异常,code: MERGE_ORDER_CHILD_CONFLICT,shopId:${order.shopId},orderId:${order.id})`;

                    throw new LoadOrderError(LoadOrderErrorCode.MERGE_ORDER_CHILD_CONFLICT, msg);
                }

                const parentItems = order.orderItems || [];
                const childOrders = order.childOrders || [];

                // 统计子订单商品
                const childProductMap = new Map<string, number>();
                let childTotalQty = 0;

                childOrders.forEach((child) => {
                    child.orderItems?.forEach((item) => {
                        const key = item.originalId;
                        const currentQty = childProductMap.get(key) || 0;
                        childProductMap.set(key, currentQty + item.quantity);
                        childTotalQty += item.quantity;
                    });
                });

                // 统计父订单商品
                const parentProductMap = new Map<string, number>();
                let parentTotalQty = 0;

                parentItems.forEach((item) => {
                    const key = item.originalId;
                    const currentQty = parentProductMap.get(key) || 0;
                    parentProductMap.set(key, currentQty + item.quantity);
                    parentTotalQty += item.quantity;
                });

                // 校验 3：总数量是否一致
                if (parentTotalQty !== childTotalQty) {
                    const msg = `订单数据异常,code: MERGE_ORDER_QUANTITY_MISMATCH,shopId:${order.shopId},orderId:${order.id})`;
                    throw new LoadOrderError(LoadOrderErrorCode.MERGE_ORDER_QUANTITY_MISMATCH, msg);
                }

                // 校验 4：种类及每种数量是否一致
                // 首先看种类数量是否一致
                if (parentProductMap.size !== childProductMap.size) {
                    const msg = `订单数据异常,code: MERGE_ORDER_ITEM_SIZE_MISMATCH,shopId:${order.shopId},orderId:${order.id})`;
                    throw new LoadOrderError(LoadOrderErrorCode.MERGE_ORDER_ITEM_SIZE_MISMATCH, msg);
                }

                // 细化比对每一种商品的数量
                for (const [orderItemOriginalId, childQty] of childProductMap.entries()) {
                    const parentQty = parentProductMap.get(orderItemOriginalId);
                    if (parentQty === undefined) {
                        const msg = `订单数据异常,code: MERGE_ORDER_MISS_ITEM,shopId:${order.shopId},orderId:${order.id})`;
                        throw new LoadOrderError(LoadOrderErrorCode.MERGE_ORDER_MISS_ITEM, msg);
                    }
                    if (parentQty !== childQty) {
                        const msg = `订单数据异常,code: MERGE_ORDER_ITEM_MISMATCH,shopId:${order.shopId},orderId:${order.id})`;
                        throw new LoadOrderError(LoadOrderErrorCode.MERGE_ORDER_ITEM_MISMATCH, msg);
                    }
                }
            }

            const orderItems = getOrderOrderItems(order);
            const orderItemOriginalIdSet = new Set(orderItems.map(({ originalId }) => originalId));

            // 至少要有一个orderItem的状态和订单的状态一致
            if (orderItems.length && !orderItems.some(({ status }) => status === order.status)) {
                const msg = `订单数据异常,code: ORDER_ITEM_STATUS_CONFLICT,shopId:${order.shopId},orderId:${order.id})`;

                throw new LoadOrderError(LoadOrderErrorCode.ORDER_ITEM_STATUS_CONFLICT, msg);
            }

            /** 有重复OrderItem */
            if (orderItems.length !== orderItemOriginalIdSet.size) {
                console.log("order:", order);

                const msg = `订单数据异常,code: ORDER_ITEM_CONFLICT,shopId:${order.shopId},orderId:${order.id})`;

                throw new LoadOrderError(LoadOrderErrorCode.ORDER_ITEM_CONFLICT, msg);
            }

            /** 已发货下有待发货的OrderItem */
            if (order.status === OrderStatus.SHIPPED) {
                if (orderItems.some(({ status }) => status === OrderStatus.PENDING_SHIPMENT)) {
                    const msg = `订单数据异常,code: SHIPPED_ORDER_HAS_PENDING_SHIP_ITEM,shopId:${order.shopId},orderId:${order.id})`;

                    throw new LoadOrderError(LoadOrderErrorCode.SHIPPED_ORDER_HAS_PENDING_SHIP_ITEM, msg);
                }
            }

            /** 商品数量不一致 */
            if (order.status !== OrderStatus.CANCELED) {
                const orderOrderItemQuantity = order.quantity;
                const orderItemQuantity = orderItems.reduce(
                    (total, orderItem) => total + (orderItem.status === OrderStatus.CANCELED ? 0 : orderItem.quantity),
                    0,
                );

                if (orderItemQuantity !== orderOrderItemQuantity) {
                    const msg = `订单数据异常,code: ORDER_QUANTITY_MISMATCH,shopId:${order.shopId},orderId:${order.id})`;

                    throw new LoadOrderError(LoadOrderErrorCode.ORDER_QUANTITY_MISMATCH, msg);
                }
            }
        } catch (error) {
            order._weirdInfo = error?.message;

            if (throwOnError) {
                throw error;
            }
        }
    }
}

/**
 * 二次处理order对象
 */
export function processOrder(order: OrderDTO) {
    if (order.mergeSplitStatus === OrderMergeSplitStatus.MERGE_PARENT) {
        // 合并订单orderItems,由于OrderTable使用子订单OrderItem构建UI,所有为了统一需要构建
        order.orderItems = buildParentOrderItems(order.childOrders);

        // 待发货状态下商品信息根据状态来排序，待发货的排在最前面
        order.childOrders.forEach((childOrder) => {
            if (childOrder.status === OrderStatus.PENDING_SHIPMENT) {
                sortOrderItemsByStatus(childOrder.orderItems);
            }
        });
    }

    if (order.status === OrderStatus.PENDING_SHIPMENT) {
        sortOrderItemsByStatus(order.orderItems);
    }

    sortEwaybillNos(order);
}

export const list = async (params: QueryOrderListParams, axiosConfig: any = {}) => {
    const newParams = trimParams(params);
    const data = await post<QueryOrderListResult>(params.shopId, "/order/list", newParams, {
        ...axiosConfig,
        pathPremiumValidator() {
            if (
                (params as QueryOrderListParamsWithPageSize)?.pageSize >= 500 ||
                (params as QueryOrderListParamsWithOffset)?.limit >= 500
            ) {
                if (
                    (params as QueryOrderListParamsWithPageSize)?.pageSize === 1000 ||
                    (params as QueryOrderListParamsWithOffset)?.limit === 1000
                ) {
                    return {
                        shopId: params.shopId,
                        type: PremiumFunctionType.ORDER_LIST_ORDER_NUMBER_1000,
                    };
                }

                return {
                    shopId: params.shopId,
                    type: PremiumFunctionType.ORDER_LIST_ORDER_NUMBER_500,
                };
            }

            if ((params as QueryOrderListParamsWithPageSize)?.tab === OrderTab.LOGISTICS_WARNING) {
                return {
                    shopId: params.shopId,
                    type: PremiumFunctionType.LOGISTICS_WARNING_ORDER_LIST,
                };
            }

            return null;
        },
    });
    const { orders } = data;

    const uniqOrders = uniqBy(orders, "id");

    uniqOrders.forEach((order) => {
        validateBackendOrder(order, false);
        processOrder(order);
    });

    data.orders = uniqOrders;

    return data;
};

interface GetMultiShopOrderListCountParams extends Omit<MultiShopOrderListParams, "offset" | "limit"> {}

interface ShopOrderCount {
    shopId: number;

    totalOrders: number;
}

export interface MultiShopOrderCount {
    shopOrderCountList: ShopOrderCount[];
    hasAbnormalOrderShopIds: number[];
}

/**
 * 批量统计多个店铺在指定查询条件下的订单总数（该接口不是/order/count的批量查询，而是不同的业务）
 */
export async function batchGetShopOrderCount(params: GetMultiShopOrderListCountParams) {
    const shopId = params.shopIds[0];
    const newParams = trimParams(params);

    return post<ShopOrderCount[]>(shopId, "/order/batch_count", newParams, {
        hideModal: true,
    });
}

/** 批量统计多个店铺在指定查询条件下的订单总数，用于跨平台打单 */
export function getMultiShopOrderCount(params: GetMultiShopOrderListCountParams) {
    const shopId = params.shopIds[0];
    const newParams = trimParams(params);

    return post<MultiShopOrderCount>(shopId, "/order/multi_shop_count", newParams, {
        hideModal: true,
    });
}
export interface BaseQuickSearchOrderParams {
    waybillNos?: string[];
    buyerUsername?: string;
    consigneeName?: string;
    phone?: string;
    sellerRemarks?: string;
    skuProperties?: string;
    orderItemExternalId?: string;
    orderItemTitle?: string;
    provinceCode?: number | string;
    cityCode?: number | string;
    districtCode?: number | string;
    originalIds?: string[];
    buyerRemarks?: string;
    productOriginalId?: string;
}

interface QuickSearchOrderParams extends BaseQuickSearchOrderParams {
    shopId: number;
}

export interface BatchQuickSearchOrderParams extends BaseQuickSearchOrderParams {
    shopIds: number[];
    /**
     * false 在一个订单找到订单后就马上给前端返回接口
     * true 则是在全部店铺查询完毕再返回,注意店铺过多导致的接口超时
     */
    searchAllShop?: boolean;
}

export interface BatchSearchOrderResult {
    shopId: number;
    orders?: OrderDTO[];
    error?: string;
}

/**
 * 同平台批量搜索订单
 */
export const batchSearchOrders = async (params: BatchQuickSearchOrderParams) => {
    let { shopIds } = params;

    const shopId = shopIds[0];

    const res = await post<BatchSearchOrderResult[]>(shopId, "/order/batch_search", params);

    const processResults: BatchSearchOrderResult[] = [];

    res.forEach((it) => {
        try {
            if (it.orders?.length) {
                it.orders.forEach((order) => {
                    validateBackendOrder(order, false);
                    processOrder(order);
                });
            }

            processResults.push(it);
        } catch (error) {
            processResults.push({ ...it, orders: [], error: error?.message });
        }
    });

    return processResults;
};

const collectOrderIds = (orders: OrderDTO[]) => {
    const orderIds: number[] = [];
    const mergeParentOrderIds: number[] = [];
    const originalIds: string[] = [];

    // TODO 未考虑拆分的订单
    orders.forEach((order) => {
        if (order.mergeSplitStatus === OrderMergeSplitStatus.MERGE_PARENT && order.childOrders) {
            mergeParentOrderIds.push(order.id);
            order.childOrders.forEach((it) => {
                orderIds.push(it.id);
                originalIds.push(it.originalId);
            });
        } else {
            orderIds.push(order.id);
            originalIds.push(order.originalId);
        }
    });

    return { orderIds, mergeParentOrderIds, originalIds };
};

/**
 * 更新订单是否打印过电子面单（用于普通面单，电子面单会在获取单号时进行更新）
 */
export const updateWaybillPrinted = (orders: OrderDTO[], template: WaybillTemplateDTO, copies?: number) => {
    const shopId = orders[0].shopId;
    const { orderIds, mergeParentOrderIds, originalIds } = collectOrderIds(orders);

    const data = {
        shopId,
        orderIds,
        mergeParentOrderIds,
        originalIds,
        logisticsCompany: template.logisticsCompany,
        templateName: template.name,
        copies,
        isHistoryOrder: isHistoryOrder(orders[0]),
    };

    return post(shopId, "/order/batch:updateWaybillPrinted", data);
};

export const updatePackingListPrinted = (orders: OrderDTO[], template: PackingListTemplateDTO, copies = 1) => {
    const shopId = orders[0].shopId;
    const { orderIds, mergeParentOrderIds } = collectOrderIds(orders);

    const data = {
        shopId,
        orderIds,
        mergeParentOrderIds,
        templateName: template.name,
        copies,
        isHistoryOrder: isHistoryOrder(orders[0]),
    };

    return retry(
        () =>
            post(shopId, "/order/batch:updatePackingListPrinted", data, {
                hideModal: true,
            }),
        3,
    );
};

// 标记订单已打印商品标签
export const updateOrderItemLabelPrinted = (orders: OrderDTO[], template: OrderItemLabelTemplateDTO, copies = 1) => {
    const shopId = orders[0].shopId;
    const { orderIds, mergeParentOrderIds } = collectOrderIds(orders);

    const data = {
        shopId,
        orderIds,
        mergeParentOrderIds,
        templateName: template.name,
        copies,
        isHistoryOrder: isHistoryOrder(orders[0]),
    };

    return retry(
        () =>
            post(shopId, "/order/batch:updateOrderItemLabelPrinted", data, {
                hideModal: true,
            }),
        3,
    );
};

export const updatePrintingStatus = (
    orders: OrderDTO[],
    isWaybillPrinted: boolean,
    isPackingListPrinted: boolean,
    isOrderItemLabelPrinted: boolean,
) => {
    const shopId = orders[0].shopId;
    const { orderIds, mergeParentOrderIds } = collectOrderIds(orders);

    const data = {
        shopId,
        orderIds,
        mergeParentOrderIds,
        isWaybillPrinted,
        isPackingListPrinted,
        isOrderItemLabelPrinted,
        isHistoryOrder: isHistoryOrder(orders[0]),
    };

    return post(shopId, "/order/batch:updatePrintingStatus", data);
};

export const updateSellerRemarks = (
    order: OrderDTO,
    flag: OrderSellerRemarksFlag | undefined | null,
    content: string,
    flagTag?: string, // 旗帜的标签内容，仅淘宝使用
) => {
    const data = {
        shopId: order.shopId,
        orderId: order.id,
        originalId: order.originalId,
        mergeSplitStatus: order.mergeSplitStatus,
        content,
        flag: null,
        flagTag,
    };

    if (flag) {
        data.flag = flag;
    }

    return post<OrderSellerRemarksDTO>(order.shopId, `/order/updateSellerRemarks`, data);
};

export interface UpdateSellerRemarksParams {
    /**
     * 备注内容
     */
    content: string;
    /**
     * 旗帜
     */
    flag?: OrderSellerRemarksFlag;
    /**
     * 旗帜的标签内容
     * 目前淘宝用到
     */
    flagTag?: string;
    /**
     * 订单合并拆分状态
     */
    mergeSplitStatus: OrderMergeSplitStatus;
    /**
     * 订单ID
     */
    orderId: number;
    /**
     * 订单编号
     */
    originalId: string;
    /**
     * 店铺ID
     */
    shopId: number;
}

export interface UpdateSellerRemarksResult {
    /**
     * 错误信息，更新失败时有值
     */
    error?: string;
    /**
     * 订单ID
     */
    orderId?: number;
    /**
     * 更新后的卖家备注信息
     */
    orderSellerRemarks?: OrderSellerRemarksDTO;
    /**
     * 店铺ID
     */
    shopId?: number;
}

export function batchUpdateSellerRemarks(shopId: number, orderSellerRemarks: UpdateSellerRemarksParams[]) {
    return post<UpdateSellerRemarksResult[]>(shopId, "/order/batch_update_seller_remarks", {
        shopId,
        orderSellerRemarks,
    });
}

export const updateWaybillNo = (order: OrderDTO, waybillNo: string) => {
    //  如果是合并订单传入子订单的orderId
    const { mergeSplitStatus } = order;

    const orderIds = [];

    if (mergeSplitStatus === OrderMergeSplitStatus.MERGE_PARENT && order.childOrders) {
        order?.childOrders?.forEach((childrenOrder) => {
            orderIds.push(childrenOrder.id);
        });
    } else {
        orderIds.push(order.id);
    }

    const data = {
        shopId: order.shopId,
        originalId: order.originalId,
        orderIds,
        waybillNo,
        isHistoryOrder: isHistoryOrder(order),
    };

    return post(order.shopId, "/order/updateWaybillNo", data);
};

export const deleteWaybillNo = (orders: OrderDTO[]) => {
    //  如果是合并订单传入子订单的orderId
    const shopId = orders[0].shopId;

    const orderIds = [];
    const originalIds = [];

    orders.forEach((order) => {
        if (order.mergeSplitStatus === OrderMergeSplitStatus.MERGE_PARENT && order.childOrders) {
            order?.childOrders?.forEach((childrenOrder) => {
                orderIds.push(childrenOrder.id);
            });
        } else {
            orderIds.push(order.id);
        }

        originalIds.push(order.originalId);
    });

    const data = {
        shopId,
        orderIds,
        originalIds,
        isHistoryOrder: isHistoryOrder(orders[0]),
    };

    return post(shopId, "/order/deleteWaybillNo", data);
};

export const updateLogisticsCompany = (orders: OrderDTO[], logisticsCompany: LogisticsCompany) => {
    const shopId = orders[0].shopId;

    const orderIds: number[] = [];

    orders.forEach((order) => {
        if (order.mergeSplitStatus === OrderMergeSplitStatus.MERGE_PARENT && order.childOrders) {
            orderIds.push(order.id);

            order.childOrders.forEach((it) => {
                orderIds.push(it.id);
            });
        } else {
            orderIds.push(order.id);
        }
    });

    const data = {
        shopId,
        orderIds,
        isHistoryOrder: isHistoryOrder(orders[0]),
        logisticsCompany,
    };

    return post(shopId, "/order/updateLogisticsCompany", data);
};

interface UpdateConsigneeParams {
    city: string;
    cityCode?: number;
    district: string;
    districtCode?: number;
    logType?: string;
    mobile: string;
    name: string;
    orderId: number;
    platformCityId?: number;
    platformDistrictId?: number;
    platformProvinceId?: number;
    platformTownId?: number;
    province: string;
    provinceCode?: number;
    shopId: number;
    street: string;
    telephone: string;
    town: string;
    zipCode: string;
    mobileChanged: boolean;
    virtualIdentifyNo?: string;
}

export const updateConsignee = (params: UpdateConsigneeParams): Promise<ManualOrderConsigneeDTO> => {
    return post(params.shopId, "/order/updateConsignee", params);
};

export const hold = (orders: OrderDTO[], restoreDate: string, remarks: string) => {
    const shopId = orders[0].shopId;
    const orderIds = orders.map((it) => it.id);

    const data = {
        shopId,
        orderIds,
        restoreDate,
        remarks,
    };

    return post(shopId, "/order/hold", data);
};

// 合并订单
export const mergeOrder = (data: MergeOrderParams) => {
    const shopId = data.shopId;

    return post<{ successful: boolean }>(shopId, "/order/merge", data);
};

export interface MergeablePendingShipmentOrder extends OrderDTO {
    mergeable: boolean;
    unmergeableReasons?: string[];
}

export interface MergeablePendingShipmentOrders {
    /**
     * 当前订单
     * 即入参orderId对应的订单信息
     */
    order?: MergeablePendingShipmentOrder;
    /**
     * 相同买家（即buyerUsername一致）可合并的待发货订单，不包含当前订单
     */
    sameBuyerMergeableOrders?: MergeablePendingShipmentOrder[];
    /**
     * 相同收货人（即consigneeHash一致但buyerUsername不一致）可合并的待发货订单，不包含当前订单
     */
    sameConsigneeMergeableOrders?: MergeablePendingShipmentOrder[];
}

export const findMergeablePendingShipmentOrder = (
    shopId: number,
    orderId: number,
): Promise<MergeablePendingShipmentOrders> =>
    get(shopId, `/order/find_mergeable_pending_shipment_orders`, { shopId, orderId });

export const restore = (orders: any) => {
    const shopId = orders[0].shopId;
    const orderIds = orders.map((it: any) => it.id);
    const data = { shopId, orderIds };
    return post(shopId, "/order/restore", data);
};

export const splitFromMergeParentOrder = (shopId: number, childOrderIds: number[]) => {
    const data = { shopId, childOrderIds };

    return post(shopId, "/order/splitFromMergeParentOrder", data);
};

export function updateOrderItemSimpleTitle(order: OrderDTO, orderItem: OrderItemDTO, simpleTitle: string) {
    const data = {
        shopId: order.shopId,
        orderId: order.id,
        orderItemId: orderItem.id,
        simpleTitle,
    };

    return post(order.shopId, "/order/updateSimpleTitle", data);
}

export function updateAllOrderItemSimpleTitle(order: OrderDTO, orderItem: OrderItemDTO, simpleTitle: string) {
    const data = {
        shopId: order.shopId,
        productOriginalId: orderItem.productOriginalId,
        skuOriginalId: orderItem.skuOriginalId,
        simpleTitle,
    };

    return post(order.shopId, "/order/updateAllSimpleTitle", data);
}

export function updateLocalSkuProperties(order: OrderDTO, orderItem: OrderItemDTO, newLocalSkuProperties: string) {
    const data = {
        shopId: order.shopId,
        orderId: order.id,
        orderItemId: orderItem.id,
        skuProperties: orderItem.skuProperties,
        oldLocalSkuProperties: orderItem.localSkuProperties,
        newLocalSkuProperties,
    };

    return post(order.shopId, "/order/updateLocalSkuProperties", data);
}

interface deleteLocalConsigneeReturn {
    cityCode: number;
    districtCode: number;
    provinceCode: number;
}

// 使用原地址
export const deleteLocalConsignee = ({ orderId, shopId }): Promise<deleteLocalConsigneeReturn> =>
    post(shopId, "/order/delete_local_consignee", { orderId, shopId });

export const cancelManualOrder = ({ orderIds, shopId }) => post(shopId, "/order/cancel", { orderIds, shopId });

// 设为主单号
export const updateMainWaybillNo = ({ ewaybillId, logisticsCompany, orderId, shopId, waybillNo }) =>
    post(shopId, "/order/updateMainWaybillNo", { ewaybillId, logisticsCompany, orderId, shopId, waybillNo });

function buildLogReprintWaybillParams(props: LogReprintWayBillParams) {
    const {
        copies,
        ewaybills,
        logisticsCompany,
        order,
        templateName,
        isGenerateWaybillNoOnly = false,
        ignoreDuplicateOrderValidation,
        generateAndPrintSameTime,
        printerName,
        shippingAddressSelectType,
    } = props;

    const orderId = order.id;
    const customPrintingOrderItemInfo = order.customPrintingOrderItemInfo;
    const shopId = order.shopId;

    const mergeSplitStatus = order.mergeSplitStatus;

    const childOrderIds = mergeSplitStatus === OrderMergeSplitStatus.MERGE_PARENT && order.childOrders ? [] : undefined;

    if (childOrderIds) {
        for (const childOrder of order.childOrders) {
            for (const orderItem of childOrder.orderItems) {
                /** @ts-ignore */
                if (orderItem.isSelected) {
                    childOrderIds.push(childOrder.id);
                    break;
                }
            }
        }
    }

    return {
        copies,
        ewaybills,
        logisticsCompany,
        orderId,
        shopId,
        templateName,
        isGenerateWaybillNoOnly,
        childOrderIds,
        mergeSplitStatus,
        ignoreDuplicateOrderValidation,
        generateAndPrintSameTime,
        customPrintingOrderItemInfo,
        printerName,
        shippingAddressSelectType,
    };
}

export const logReprintWaybill = (props: LogReprintWayBillParams) => {
    const data = buildLogReprintWaybillParams(props);

    let path = "/order/logReprintWaybill";

    if (props.invocationSource === PrintShippingSource.SCAN_PRINT) {
        path = "/scan_printing/ewaybill/logReprintWaybill";
    }

    return post<{ successful: boolean }>(data.shopId, path, data);
};

interface DecryptedConsignee extends OrderConsigneeDTO {
    /** 是否为虚拟号 */
    privacyProtection?: boolean;
    /** 区号 */
    virtualIdentifyNo?: string;
}

export type ListDecryptedConsigneeResult = { id: number } & { consignee: DecryptedConsignee };

interface DecryptedConsigneeOrderDto {
    id: number;
    mergeSplitStatus: OrderMergeSplitStatus;
    originalId: string;
}

interface ListDecryptedConsigneeParams {
    shopId: number;
    orders: OrderDTO[];

    /** 由发货易进行加密的店铺都需要传 */
    captchaKey?: string;
    captchaText?: string;

    /**
     * 淘宝平台可传true，其他平台传入无效
     */
    decryptNameAddressOnly?: boolean;
}

// 判断本次手工订单解密是否需要验证码
export async function checkDecryptCaptchaRequired(shopId: number) {
    return await post<{ success: boolean }>(shopId, `/order/v2/check_decrypt_captcha_required?shopId=${shopId}`);
}

// 查看订单完整信息
export async function listDecryptedConsignee(
    params: ListDecryptedConsigneeParams,
): Promise<ListDecryptedConsigneeResult[]> {
    const { shopId, orders, decryptNameAddressOnly, captchaKey, captchaText } = params;

    const orderMapping = new Map<number, OrderDTO>();
    const orderDtos: DecryptedConsigneeOrderDto[] = orders.map((order) => {
        orderMapping.set(order.id, cloneDeep(order));

        return {
            id: order.id,
            originalId: order.originalId,
            mergeSplitStatus: order.mergeSplitStatus,
        };
    });

    const results = await post<ListDecryptedConsigneeResult[]>(shopId, "/order/list_decrypted_consignee", {
        shopId,
        orderDtos,
        decryptNameAddressOnly,
        captchaKey,
        captchaText,
    });

    // 后端返回并非完整的收货人信息
    results.forEach((result) => {
        const { id, consignee } = result;
        const order = orderMapping.get(id);

        if (order) {
            result.consignee = { ...order.consignee, ...consignee };
        }
    });

    return results;
}

export async function listDecryptedConsigneeForPrint(
    params: ListDecryptedConsigneeParams,
): Promise<ListDecryptedConsigneeResult[]> {
    const { shopId, orders, decryptNameAddressOnly, captchaKey, captchaText } = params;

    const orderMapping = new Map<number, OrderDTO>();
    const orderDtos: DecryptedConsigneeOrderDto[] = orders.map((order) => {
        orderMapping.set(order.id, cloneDeep(order));

        return {
            id: order.id,
            originalId: order.originalId,
            mergeSplitStatus: order.mergeSplitStatus,
        };
    });

    const results = await post<ListDecryptedConsigneeResult[]>(shopId, "/order/list_decrypted_consignee_for_print", {
        shopId,
        orderDtos,
        decryptNameAddressOnly,
        captchaKey,
        captchaText,
    });

    // 后端返回并非完整的收货人信息
    results.forEach((result) => {
        const { id, consignee } = result;
        const order = orderMapping.get(id);

        if (order) {
            result.consignee = { ...order.consignee, ...consignee };
        }
    });

    return results;
}

export interface ChildOrderProfile {
    orderItemProfiles: OrderItemProfile[];

    /**
     * @todo 这个参数是不是没有用了？
     */
    orderGiftItemProfiles?: GiftOrderItemProfile[];

    ewaybills: EwaybillDTO[];
}

export interface OrderItemProfile {
    orderItemId: number;

    quantity: number;
}

export interface GiftOrderItemProfile {
    orderGiftItemId: number;

    quantity: number;
}

export interface SplitOrderBody {
    shopId: number;
    orderId: number;
    profiles: ChildOrderProfile[];
    originalId: string;
    mergeSplitStatus: OrderMergeSplitStatus;
}

export function splitOrder(shopId: number, body: SplitOrderBody) {
    return post(shopId, "/order/splitOrder", body, {
        pathPremiumValidator() {
            return {
                shopId: shopId,
                type: PremiumFunctionType.ORDER_SPLITTING,
            };
        },
    });
}

export interface BatchSplitOrderResult {
    orders: { errorMsg: string; orderId: number; successful: boolean }[];
}

export function batchSplitOrder(shopId: number, body: SplitOrderBody[]) {
    return post<BatchSplitOrderResult>(shopId, "/order/batchSplitOrder", body, {
        pathPremiumValidator() {
            return {
                shopId: shopId,
                type: PremiumFunctionType.ORDER_SPLITTING,
            };
        },
    });
}

/**
 * 取消订单拆分，将所有子订单删除，将订单还原回独立订单状态
 */
export function cancelSplitOrder(shopId: number, parentOrderId: number, hideModal = false) {
    return post(
        shopId,
        "/order/cancel_split",
        {
            shopId,
            parentOrderId,
        },
        {
            hideModal,
            pathPremiumValidator() {
                return {
                    shopId: shopId,
                    type: PremiumFunctionType.ORDER_SPLITTING,
                };
            },
        },
    );
}

export interface AddOrDeleteMarkToOrderParams {
    markId: number;
    orderIds: number[];
    shopId: number;
}

/**
 * 给订单增加标记
 */
export function addMarkToOrder(params: AddOrDeleteMarkToOrderParams) {
    return post<{ successful: boolean }>(params.shopId, "/order/update_mark", params);
}

/**
 * 删除订单的标记
 */
export function deleteMarkToOrder(params: Omit<AddOrDeleteMarkToOrderParams, "markId">) {
    return post<{ successful: boolean }>(params.shopId, "/order/delete_mark", params);
}

/**
 * 店铺概述
 */

export interface ShopOverviewResult {
    /**
     * 刷新令牌过期时间
     */
    accessTokenExpiringTime?: number;
    /**
     * 是否有异常订单
     * （统计订单数量需要考虑合并的情况比较麻烦，所以不统计数量）
     * 该店铺是否有异常订单
     */
    hasAbnormalOrders?: boolean;
    /**
     * 店铺预期删除的时间
     */
    pendingDeletedTime?: string;
    /**
     * 该店铺即将揽收超时订单总数
     * 订单发货后即将超过平台规定揽收时间还没有物流轨迹的订单数量
     */
    pickupDeadlineWarningOrderCount?: number;
    /**
     * 该店铺即将发货超时订单总数
     */
    shippingDeadlineOrderCount?: number;
    /**
     * 店铺ID
     */
    shopId?: number;
    /**
     * 店铺到期时间
     */
    subscriptionEndTime?: string;
    /**
     * 该店铺物流预警订单总数
     */
    totalLogisticsWarningOrders?: number;
    /**
     * 该店铺延迟发货的订单总数
     */
    totalOnHoldOrders?: number;
    /**
     * 该店待发货订单总数
     */
    totalPendingShipmentOrders?: number;
    /**
     * 该店铺待发货退款中的订单总数
     */
    totalRefundingOrders?: number;

    isAnyShopHasAbnormalOrder?: boolean;
}

export function getShopOverview(shopId, shopIdsString: string): Promise<ShopOverviewResult[]> {
    return get(shopId, "/order/overview", { shopIdsString });
}

/**
 * 删除订单异常信息
 * 传需要删除的订单id
 */
export function deleteAbnormalRecord(orders: OrderDTO[]): Promise<void> {
    const rawOrderIds = [];

    const shopId = orders?.[0].shopId;

    orders.forEach((order) => {
        rawOrderIds.push(order.id);
    });

    const orderIds = uniq(rawOrderIds);

    return post(shopId, "/order/delete_abnormal_records", { orderIds, shopId });
}

/**
 * 设为已有物流信息 参数orderIds：如果是合并订单，需要同时将所有子订单的id加进来
 */

export function setHasLogistcsInfo(orders: OrderDTO[]) {
    const shopId = orders[0].shopId;

    const rawOrderIds = [];

    orders.forEach((order) => {
        const { mergeSplitStatus, id, childOrders } = order;

        rawOrderIds.push(id);

        if (mergeSplitStatus === OrderMergeSplitStatus.MERGE_PARENT) {
            childOrders?.forEach((childOrder) => {
                rawOrderIds.push(childOrder.id);
            });
        }
    });

    const orderIds = uniq(rawOrderIds);

    return post(shopId, "/order/set_has_logistcs_info", { shopId, orderIds });
}

interface SequenceNoOrderDtoParams {
    childOrderIds: number[];

    id: number;

    mergeSplitStatus: OrderMergeSplitStatus;
}

interface SequenceNoParams {
    orderIds: number[];
}

export interface GenerateSequenceNoResult {
    orderId: number;
    sequenceNo: number;
}

/**
 * 生成订单打印流水号
 * 打印面单时，如果模板中添加了'流水号'字段，从Order.sequenceNo获取打印流水号，如果Order.sequenceNo为null，则调用该接口生成流水号，
 * 打印电子面单时，该接口的调用应该在获取电子面单单号接口前调用，避免调用该接口失败导致浪费运单号
 */
export function generateSequenceNo(params: SequenceNoParams) {
    return retry(
        () =>
            mainServerClient.post<GenerateSequenceNoResult[]>("/order/generate_sequence_no", params, {
                hideModal: true,
            }),
        3,
    );
}

interface UpdateOrderSequenceNoParams {
    shopId: number;
    sequenceNoValues: {
        orderId: number;
        sequenceNo: number;
    }[];
}

/**
 * 调用node系统接口更新订单的打印流水号
 */
export function updateOrderSequenceNo(params: UpdateOrderSequenceNoParams) {
    return retry(
        () =>
            post(params.shopId, "/order/update_sequence_no", params, {
                hideModal: true,
            }),
        3,
    );
}

interface CancelMergeParams {
    /**取消合并的父订单 */
    parentOrderIds: number[];

    shopId: number;
}

/**
 * 批量取消合并订单，取消合并的订单不会再自动合并
 */
export function batchCancelMergeOrders(params: CancelMergeParams) {
    return post(params.shopId, "/order/cancel_merge", params);
}

interface GetOrderCountParams {
    shopId: number;

    tab: OrderTab;
}

/**
 * 获取订单列表订单数统计
 */
export function getOrderListCount(params: GetOrderCountParams) {
    return post<OrderListCount>(params.shopId, "/order/count", params);
}

export interface AbnormalCount {
    shopId: number;
    totalCount: number;
    details: AbnormalCountDetail[];
}

export interface AbnormalCountDetail {
    /**
     * 异常类型
     */
    abnormalType?: string;
    /**
     * 异常类型描述（前端可直接用于展示）
     */
    abnormalTypeDesc?: string;
    /**
     * 该异常类型对应的异常数量
     */
    count?: number;
}

/**
 * 批量获取店铺异常订单信息
 */
export function batchGetAbnormalCount(shopIds: number[], config?) {
    return post<AbnormalCount[]>(shopIds[0], "/order/abnormal_count", { shopIds }, config);
}

interface BatchUpdateShippingAddressParams {
    orderIds: number[];
    shippingAddressId: number;
    shopId: number;
    shipperName: string;
}

/**
 * 批量修改发货人
 */
export function batchUpdateShippingAddress(params: BatchUpdateShippingAddressParams): Promise<{ successful: boolean }> {
    return post(params.shopId, "/order/batch_update_shipping_address", params);
}

export interface SimpleProduct {
    externalId?: string;

    originalId: string;

    title: string;

    titles?: string[];

    simpleTitle?: string;

    skus: SimpleSku[];

    shopId: number;

    picture: string;
}

export interface SimpleSku {
    /** 注意:不一定唯一  */
    originalId: string;
    /** 注意:不一定唯一  */
    propertiesName: string;

    externalId?: string;

    /** 该值唯一 */
    id: string;

    picture?: string;

    simpleTitle?: string;
}

export interface GetOrderLatestProductsParams {
    shopIds: number[];
    hasPendingShipmentOrder: boolean;
    loadProductSimpleTitle: boolean;
    loadSkuSimpleTitle: boolean;
    loadProductPicture: boolean;
    hasShippedOrder: boolean;
    hasSuccessOrder: boolean;
}

/**
 * 获取最新的商品信息，用于订单列表，商品名称及商品属性搜索框
 */
export function getOrderLatestProducts(params: GetOrderLatestProductsParams) {
    const shopId = params.shopIds[0];

    let needSimpleTitle = params.loadProductSimpleTitle;
    let needSkuSimpleTitle = params.loadSkuSimpleTitle;

    return post<SimpleProduct[]>(shopId, "/order/latest_products", {
        hasPendingShipmentOrder: params.hasPendingShipmentOrder,
        hasShippedOrder: params.hasShippedOrder,
        hasSuccessOrder: params.hasSuccessOrder,
        loadProductSimpleTitle: params.loadProductSimpleTitle,
        loadSkuSimpleTitle: params.loadSkuSimpleTitle,
        loadProductPicture: params.loadProductPicture,
        shopIds: params.shopIds,
        needSimpleTitle: needSimpleTitle,
        needSkuSimpleTitle: needSkuSimpleTitle,
    });
}

/**
 * 平台sdk（淘宝,抖店）或网关（pdd）解密订单的话，解密之后掉这个接口保存下操作记录
 */
export function postLogOrderDecryptConsigneeByPlatformApi(shopId: number, orderDtos: DecryptedConsigneeOrderDto[]) {
    return post(shopId, "/order/log_decrypt_consignee", {
        shopId,
        orderDtos,
    });
}

export function getAllAffiliateMarketersByShop(shopId: number) {
    return get<AffiliateMarketerDto[]>(shopId, "/order/list_all_affiliate_marketer", { shopId }).then((data) => {
        data.forEach((item) => {
            if (item.originalId) {
                item.originalIdStr = item.originalId.toString();
            }
        });

        return data;
    });
}

export function lockOrder(shopId: number, orders: OrderDTO[]) {
    const orderIds = [];

    orders.forEach((order) => {
        orderIds.push(order.id);

        if (order.mergeSplitStatus === OrderMergeSplitStatus.MERGE_PARENT && order.childOrders) {
            order.childOrders.forEach(({ id }) => {
                orderIds.push(id);
            });
        }
    });

    return post(shopId, "/order/order/lock", { shopId, orderIds });
}

export function unlockOrder(shopId: number, orders: OrderDTO[]) {
    const orderIds = [];

    orders.forEach((order) => {
        orderIds.push(order.id);

        if (order.mergeSplitStatus === OrderMergeSplitStatus.MERGE_PARENT && order.childOrders) {
            order.childOrders.forEach(({ id }) => {
                orderIds.push(id);
            });
        }
    });

    return post(shopId, "/order/order/unlock", { shopId, orderIds });
}

/**
 * 更新订单的商品信息
 * 1个小时内只能更新一次
 */
export function updateLatestProducts(shopId: number, hideModal?: boolean) {
    return post(
        shopId,
        "/order/update_latest_products",
        {
            shopId,
        },
        {
            hideModal,
        },
    );
}

export interface UpdateOrderItemExtraInfoOrderDto {
    orderId: number;
    mergeSplitStatus: OrderMergeSplitStatus;
    orderItems: UpdateOrderItemExtraInfoOrderItemDto[];
}

export interface UpdateOrderItemExtraInfoOrderItemDto {
    orderItemId: number;
    imeis?: string[];
    imei2s?: string[];
    deviceSns?: string[];
    eanCodes?: string[];
    orderItemOriginalId: string;
}

export interface UpdateOrderItemExtraInfoParams {
    shopId: number;
    orders: UpdateOrderItemExtraInfoOrderDto[];
}

export function updateOrderItemExtraInfo(params: UpdateOrderItemExtraInfoParams) {
    return post(params.shopId, "/order/order_item/extra_info", params);
}

/**
 * 仅淘宝店铺可用
 */
export function getOrderConsignee(shopId: number, orderId: number): Promise<OrderConsigneeDTO> {
    return get(shopId, "/order/get_consignee", { shopId, orderId });
}

interface AsyncOperationTaskTaskResult {
    error?: string;
    /**
     * 任务结果
     */
    result?: { [key: string]: any };

    shopId?: number;

    taskId?: string;
    /**
     * 任务状态
     */
    taskStatus?: TaskStatusEnum;
}

export enum AutoMergeMergerableOrderScene {
    ENABLE_AUTO_MERGE_SETTING = "ENABLE_AUTO_MERGE_SETTING",
    ORDER_LIST_OTHER_OPERATION = "ORDER_LIST_OTHER_OPERATION",
    RECOVER_HOLDING_ORDER = "RECOVER_HOLDING_ORDER",
}

interface AutoMergeMergerableOrdersParams {
    shopId: number;
    autoMergeManualIndependentOrder?: boolean;
    orderIds?: number[];
    /** 使用的场景 */
    scene: AutoMergeMergerableOrderScene;
    orderListParams?: QueryOrderListParams;
}

/** 用于用户开启“自动合并”功能后对现存未合并的订单进行自动合并 */
export function autoMergeMergerableOrders(params: AutoMergeMergerableOrdersParams) {
    return post<AsyncOperationTaskTaskResult>(params.shopId, "/order/async_auto_merge_mergerable_orders", params);
}

export function getAutoMergerResult(shopId: number, taskId: string) {
    return get<AsyncOperationTaskTaskResult>(shopId, "/order/get_auto_merger_result", { taskId });
}

export interface ManualMergeableOrderCountOrderDto {
    /**
     * 买家用户名
     */
    buyerUsername: string;
    /**
     * 收货人hash
     */
    consigneeHash: string;
    /**
     * 订单id
     */
    id: number;
}

export interface ManualMergeableOrderCount {
    /**
     * 该买家（即buyerUsername一致）的待付款订单数
     */
    sameBuyerPendingPaymentCount?: number;
    /**
     * 该买家（即buyerUsername一致）可合并的待发货订单数（不包含当前订单）
     */
    sameBuyerMergeablePendingShipmentCount?: number;
    /**
     * 买家用户名
     */
    buyerUsername?: string;
    /**
     * 收货人hash
     */
    consigneeHash?: string;
    /**
     * 该收货人（即consigneeHash一致但buyerUsername不一致）的待付款订单数
     */
    sameConsigneePendingPaymentCount?: number;
    /**
     * 该收货人（即consigneeHash一致但buyerUsername不一致）可合并的待发货订单数（不包含当前订单）
     */
    sameConsigneeMergeablePendingShipmentCount?: number;
    /**
     * 订单ID
     */
    orderId?: number;
    /**
     * 店铺ID
     */
    shopId?: number;
}

/** 获取可手工合并订单数量 */
export function getManualMergeableOrderCount(shopId: number, orders: ManualMergeableOrderCountOrderDto[]) {
    return post<ManualMergeableOrderCount[]>(shopId, "/order/get_manual_mergeable_order_count", { shopId, orders });
}

export interface UpdatedOrderItemInfo {
    /**
     * 待修改的订单项 ID。
     */
    orderItemOriginalId?: number;
    /**
     * 修改后的商品图片。
     */
    picture?: string;
    /**
     * 修改后的商品单价。
     */
    price?: number;
    /**
     * 修改后的商品商家编码。
     */
    productExternalId?: string;
    /**
     * 修改后的商品数量。
     */
    quantity?: number;
    /**
     * 修改后的 SKU 商家编码。
     */
    skuExternalId?: string;
    /**
     * 修改后的商品属性。
     */
    skuProperties?: string;
    /**
     * 修改后的商品标题。
     */
    title?: string;
    /**
     * 字段更新标识，指示哪些字段需要更新。
     */
    updateFlags?: FieldUpdateFlags;
    /**
     * 修改后的商品重量，单位克（g）。
     */
    weight?: number;
}

/**
 * 字段更新标识，指示哪些字段需要更新。
 *
 * FieldUpdateFlags
 */
export interface FieldUpdateFlags {
    updatePicture?: boolean;
    updatePrice?: boolean;
    updateProductExternalId?: boolean;
    updateQuantity?: boolean;
    updateSkuExternalId?: boolean;
    updateSkuProperties?: boolean;
    updateTitle?: boolean;
    updateWeight?: boolean;
}

export function updateManualOrderItem(shopId: number, orderId: number, updatedOrderItemInfos: UpdatedOrderItemInfo[]) {
    return post(shopId, "/order/manual/update_order_item", { shopId, orderId, updatedOrderItemInfos });
}

interface PrintingPrevalidationOrderItem {
    /**
     * 商品项ID
     */
    id: number;
    /**
     * 商品项原始ID
     */
    originalId: string;
    /**
     * 商品项状态
     */
    status?: OrderStatus;
}

interface PrintingPrevalidationChildOrder {
    /**
     * 子订单ID
     */
    id: number;
    /**
     * 子订单编号
     */
    originalId: string;
    /**
     * 子订单状态
     */
    status: OrderStatus;
}

export interface PrintingPrevalidationOrder {
    /**
     * 子订单列表
     * 合并父订单时需回传
     */
    childOrders: PrintingPrevalidationChildOrder[];
    /**
     * 收货人hash，用于判断收货人是否发生变化
     */
    consigneeHash: string;
    /**
     * 订单ID
     */
    id: number;
    /**
     * 订单合并拆分状态
     */
    mergeSplitStatus: OrderMergeSplitStatus;
    /**
     * 是否打印过商品标签
     * 如果无需校验商品标签打印状态需传null，例如打印快递单前的校验（和商品标签是否打印无关）该字段传null即可
     */
    orderItemLabelPrinted?: boolean;
    /**
     * 订单项列表
     */
    orderItems?: PrintingPrevalidationOrderItem[];
    /**
     * 订单编号
     */
    originalId?: string;
    /**
     * 是否已打印发货单，可用于发货单打印前校验
     * 如果无需校验发货单打印状态需传null，例如打印快递单前的校验（和发货单是否打印无关）该字段传null即可
     */
    packingListPrinted?: boolean;
    /**
     * 订单的退款状态
     */
    refunding: boolean;
    /**
     * 店铺ID
     */
    shopId: number;
    /**
     * 订单状态
     */
    status: OrderStatus;

    /**
     * 是否已打印快递单，可用于普通快递单打印前校验
     * 如果无需校验快递单打印状态需传null，例如打印发货单前的校验（和快递单是否打印无关）该字段传null即可
     */
    waybillPrinted?: boolean;
}

interface PrintingPrevalidationResultOrder {
    /**
     * 错误信息 如果为null时表示可以打印
     */
    error?: string;
    id: number;
}

export interface PrintingPrevalidationResult {
    shopId: number;
    orders: PrintingPrevalidationResultOrder[];
}

/**
 * 打印前订单数据校验(目前仅在打印发货单中使用)
 */
export function validateBeforePickPrint(shopId: number, orders: PrintingPrevalidationOrder[]) {
    return post<PrintingPrevalidationResult>(shopId, "/order/validate_before_printing", { shopId, orders });
}

interface ReportOrderAllowedCallNoParams {
    shopId: number;
    orderId: number;
    virtualNo: string;
    allowedCallNos: string[];
}

export function reportOrderAllowedCallNo(params: ReportOrderAllowedCallNoParams) {
    return post(params.shopId, "/order/report_allowed_call_no", params);
}

interface InvisiblePlatformOrderSummary {
    appVisibleOrders?: OrderBriefInfo[];
    platformOrder?: OrderBriefInfo;
}

export interface OrderBriefInfo {
    isOnHold?: boolean;
    isRefunding?: boolean;
    mergeSplitStatus?: OrderMergeSplitStatus;
    originalId?: string;
    status?: OrderStatus;
}

export interface InvisiblePlatformOrderResult {
    /** 前端值 */
    shopId: number;

    /** 前端值 */
    tab: OrderTab;

    /**
     * 平台订单信息摘要列表（分页后的结果）
     */
    orders?: InvisiblePlatformOrderSummary[];
    /**
     * 被统计但在列表中不可见的平台订单总数
     */
    totalCount?: number;
}

export function getInVisiblePlatformOrders(shopId: number, tab: OrderTab) {
    return post<InvisiblePlatformOrderResult>(shopId, "/order/list_invisible_platform_orders", {
        shopId,
        tab,
        pageSize: 500,
        pageNo: 1,
    });
}

/**
 * @deprecated
 */
const orderApi = {
    list,
    updateWaybillPrinted,
    updatePackingListPrinted,
    updateOrderItemLabelPrinted,
    updatePrintingStatus,
    updateWaybillNo,
    deleteWaybillNo,
    updateLogisticsCompany,
    updateSellerRemarks,
    updateConsignee,
    hold,
    restore,
    splitFromMergeParentOrder,
    updateOrderItemSimpleTitle,
    updateAllOrderItemSimpleTitle,
    updateLocalSkuProperties,
    cancelManualOrder,
    updateMainWaybillNo,
    splitOrder,
    batchSplitOrder,
    cancelSplitOrder,
    mergeOrder,
    deleteLocalConsignee,
    logReprintWaybill,
    listDecryptedConsignee,
    addMarkToOrder,
    deleteMarkToOrder,
    batchCancelMergeOrders,
};

export default orderApi;
